import { projectId, publicAnonKey } from '/utils/supabase/info';

type Cached<T> = {
  value: T;
  expiresAt: number;
};

type UserProfilePayload = {
  profile?: {
    plan?: string;
    subscriptionStatus?: string | null;
    photo_url?: string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const CACHE_TTL_MS = 45_000;
const cache = new Map<string, Cached<UserProfilePayload>>();
const inFlight = new Map<string, Promise<UserProfilePayload>>();

function key(accessToken: string): string {
  return `user-profile:${accessToken.slice(0, 24)}`;
}

function authHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${publicAnonKey}`,
    'X-User-Token': accessToken,
  };
}

async function fetchUserProfileFromApi(accessToken: string): Promise<UserProfilePayload> {
  const res = await fetch(
    `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/user-profile`,
    {
      headers: authHeaders(accessToken),
    },
  );
  if (!res.ok) {
    throw new Error(`user-profile ${res.status}`);
  }
  return (await res.json()) as UserProfilePayload;
}

export function invalidateUserProfileCache(accessToken?: string | null): void {
  if (accessToken && accessToken.trim()) {
    const k = key(accessToken.trim());
    cache.delete(k);
    inFlight.delete(k);
    return;
  }
  cache.clear();
  inFlight.clear();
}

export async function getUserProfileCached(
  accessToken: string,
  options?: { force?: boolean },
): Promise<UserProfilePayload> {
  const token = accessToken.trim();
  const k = key(token);
  const force = !!options?.force;
  const now = Date.now();

  if (!force) {
    const cached = cache.get(k);
    if (cached && cached.expiresAt > now) {
      return cached.value;
    }
  }

  const existing = inFlight.get(k);
  if (existing) return existing;

  const req = fetchUserProfileFromApi(token)
    .then((value) => {
      cache.set(k, { value, expiresAt: Date.now() + CACHE_TTL_MS });
      return value;
    })
    .finally(() => {
      inFlight.delete(k);
    });

  inFlight.set(k, req);
  return req;
}
